/**
 * Help Mode Provider
 * 
 * Context provider for Help Mode state management.
 * Provides global state for help mode activation, content highlighting,
 * and chat opening functionality.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useChatManager } from '../ChatManager';

const HelpModeContext = createContext();

export const useHelpMode = () => {
  const context = useContext(HelpModeContext);
  if (!context) {
    throw new Error('useHelpMode must be used within a HelpModeProvider');
  }
  return context;
};

export const HelpModeProvider = ({ children }) => {
  const [isHelpMode, setIsHelpMode] = useState(false);
  const [highlightedContent, setHighlightedContent] = useState([]);
  const { openChat } = useChatManager();

  const toggleHelpMode = useCallback(() => {
    setIsHelpMode(prev => !prev);
  }, []);

  const enableHelpMode = useCallback(() => {
    setIsHelpMode(true);
  }, []);

  const disableHelpMode = useCallback(() => {
    setIsHelpMode(false);
  }, []);

  const highlightContent = useCallback((content) => {
    // Parse and highlight workout content
    // This could be enhanced to parse content and identify clickable elements
    setHighlightedContent(content);
  }, []);

  const handleContentClick = useCallback((instruction, workoutContext) => {
    // Open chat with instruction pre-filled
    if (instruction && instruction.trim().length > 5) {
      openChat({
        instruction: instruction.trim(),
        workoutId: workoutContext?.workoutId,
        workoutContext: workoutContext?.workoutContext,
        title: workoutContext?.title,
        subtitle: workoutContext?.subtitle
      });
    }
  }, [openChat]);

  const clearHighlightedContent = useCallback(() => {
    setHighlightedContent([]);
  }, []);

  const value = {
    isHelpMode,
    toggleHelpMode,
    enableHelpMode,
    disableHelpMode,
    highlightContent,
    handleContentClick,
    highlightedContent,
    clearHighlightedContent
  };

  return (
    <HelpModeContext.Provider value={value}>
      {children}
    </HelpModeContext.Provider>
  );
};

